/**
 * Hook for Padrón General report.
 * Year filter persisted in URL. All other filtering handled by the table.
 */

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getAllMinisters,
  getMinisterIdsInActiveRequests,
  getLatestStatusNotesByMinister,
} from "../services/reports.service";
import { REPORT_QUERY_KEYS } from "../queryKeys";
import type { MinisterRegistryEntry, MinisterRegistryFilters } from "../types";

const CURRENT_YEAR = new Date().getFullYear();

function readFilters(params: URLSearchParams): MinisterRegistryFilters {
  return {
    year: parseInt(params.get("year") ?? String(CURRENT_YEAR)),
  };
}

export function useMinisterRegistryReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);

  // Minister roster changes only via writes — Infinity staleTime is safe.
  // Mutation invalidation in useMinister.ts must also invalidate "ministerRegistry".
  // gcTime > staleTime so cache survives navigation without refetching.
  const { data: rawMinisters = [], isLoading: isLoadingMinisters } = useQuery({
    queryKey: REPORT_QUERY_KEYS.ministerRegistry,
    queryFn: getAllMinisters,
    staleTime: Infinity,
    gcTime: 4 * 60 * 60 * 1000,   // 4 hours in memory
    refetchOnWindowFocus: false,
  });

  const { data: activeRequestIds = new Set<string>(), isLoading: isLoadingRequests } =
    useQuery({
      queryKey: ["ministerActiveRequestIds", filters.year],
      queryFn: () => getMinisterIdsInActiveRequests(filters.year),
      staleTime: 60 * 60 * 1000,  // 1 hour — credential state stable within a session
      gcTime: 2 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const { data: statusNoteMap = new Map<string, string>(), isLoading: isLoadingNotes } =
    useQuery({
      queryKey: REPORT_QUERY_KEYS.ministerStatusNotes,
      queryFn: getLatestStatusNotesByMinister,
      staleTime: 2 * 60 * 60 * 1000, // 2 hours — history notes rarely change
      gcTime: 4 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const isLoading = isLoadingMinisters || isLoadingRequests || isLoadingNotes;

  // Merge: enrich each minister with credential + note info for selected year.
  const entries = useMemo<MinisterRegistryEntry[]>(() => {
    return rawMinisters.map((m) => ({
      ...m,
      latest_status_note: statusNoteMap.get(m.id) ?? null,
      missing_from_credentials: m.is_active && !activeRequestIds.has(m.id),
    }));
  }, [rawMinisters, activeRequestIds, statusNoteMap]);

  // Summary always reflects ALL ministers (global totals, year-aware for credentials).
  const summary = useMemo(() => {
    const total = entries.length;
    const active = entries.filter((m) => m.is_active).length;
    const inactive = total - active;
    const missingCredentials = entries.filter(
      (m) => m.is_active && m.missing_from_credentials
    ).length;

    const byReason = new Map<string, number>();
    entries.forEach((m) => {
      if (!m.is_active && m.inactive_reason_code) {
        byReason.set(
          m.inactive_reason_code,
          (byReason.get(m.inactive_reason_code) ?? 0) + 1
        );
      }
    });

    return { total, active, inactive, missingCredentials, byReason };
  }, [entries]);

  const updateFilter = <K extends keyof MinisterRegistryFilters>(
    key: K,
    value: MinisterRegistryFilters[K]
  ) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const strVal = String(value);
        if (key === "year" && strVal === String(CURRENT_YEAR)) {
          next.delete(key);
        } else {
          next.set(key, strVal);
        }
        return next;
      },
      { replace: true }
    );
  };

  const resetFilters = () => setSearchParams({}, { replace: true });

  return { entries, filters, summary, isLoading, updateFilter, resetFilters };
}
